import React, { useCallback, useEffect, useMemo, useState } from "react";
import { X, CreditCard, Wallet, Clock, CheckCircle, XCircle, RefreshCw, User, AlertCircle, Loader2, FileText, } from "lucide-react";
import { getMembershipPlans } from "../../api/membershipPlanService";
import { getMemberSubscriptions, createMemberSubscription, verifySubscriptionPayment } from "../../api/subscriptionApi";
import SubscriptionInvoice from "./SubscriptionInvoice";

const unwrapData = (response) => {
    const data = response?.data?.data ?? response?.data ?? [];
    return data;
};

const isActivePlan = (status) => {
    return (
        String(status).toLowerCase() === "active" ||
        String(status) === "1"
    );
};

const formatAmount = (amount) => {
    return Number(amount || 0).toLocaleString("en-IN", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
};

const formatDate = (date) => {
    if (!date) return "-";
    const parsedDate = new Date(date);
    if (Number.isNaN(parsedDate.getTime())) return "-";
    return parsedDate.toLocaleDateString("en-IN", {
        day: "2-digit",
        month: "short",
        year: "numeric",
    });
};

const statusClass = (status) => {
    const value = String(status || "").toLowerCase();
    if (["success", "active", "completed"].includes(value)) {
        return "bg-green-100 text-green-700";
    }
    if (["failed", "rejected", "expired"].includes(value)) {
        return "bg-red-100 text-red-700";
    }
    return "bg-yellow-100 text-yellow-700";
};

const MemberSubscriptionModal = ({ member, onClose, onSuccess, showPopup }) => {
    const [membershipPlans, setMembershipPlans] = useState([]);
    const [subscriptions, setSubscriptions] = useState([]);
    const [selectedPlan, setSelectedPlan] = useState("");
    const [paymentMode, setPaymentMode] = useState("cash");
    const [remarks, setRemarks] = useState("");
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);
    const [verifyingId, setVerifyingId] = useState(null);
    const [error, setError] = useState("");
    const [verificationItem, setVerificationItem] = useState(null);
    const [verificationStatus, setVerificationStatus] = useState(null);
    const [invoiceItem, setInvoiceItem] = useState(null);
    const memberId = member?.rfId;
    const memberName = member?.name || member?.full_name || member?.member_name || "-";
    const memberMobile = member?.mobile_number || member?.mobile || member?.phone_number || "-";
    const selectedPlanData = useMemo(() => {
        return membershipPlans.find(
            (plan) => String(plan.rfId) === String(selectedPlan)
        );
    }, [membershipPlans, selectedPlan]);
    const pricing = selectedPlanData?.pricing;
    const finalAmount = pricing?.final_amount !== undefined ? Number(pricing.final_amount) : Number(selectedPlanData?.amount || 0);
    const loadData = useCallback(async () => {
        if (!memberId) return;
        try {
            setLoading(true);
            setError("");
            const [planResponse, subscriptionResponse] = await Promise.all([
                getMembershipPlans(),
                getMemberSubscriptions(memberId),
            ]);
            const planData = unwrapData(planResponse);
            const subscriptionData = unwrapData(subscriptionResponse);
            const activePlans = Array.isArray(planData) ? planData.filter((plan) => isActivePlan(plan.status)) : [];
            setMembershipPlans(activePlans);
            setSubscriptions(
                Array.isArray(subscriptionData) ? subscriptionData : []
            );
            setSelectedPlan((previous) => {
                const exists = activePlans.some(
                    (plan) => String(plan.rfId) === String(previous)
                );
                if (exists) return previous;
                return activePlans.length > 0 ? String(activePlans[0].rfId) : "";
            });
        } catch (err) {
            setError(err?.response?.data?.message || err?.message || "Unable to load membership details."
            );
        } finally {
            setLoading(false);
        }
    }, [memberId]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const handleCreateSubscription = async (e) => {
        e.preventDefault();
        if (!selectedPlan) {
            showPopup?.("error", "Validation", "Please select an active membership plan.");
            return;
        }
        try {
            setCreating(true);
            const payload = {
                member_id: memberId,
                membership_plan_id: Number(selectedPlan),
                payment_mode: paymentMode,
                remarks: remarks.trim(),
            };
            const response = await createMemberSubscription(payload);
            if (response?.data?.success === false) {
                throw new Error(
                    response?.data?.message || "Subscription creation failed."
                );
            }
            showPopup?.(
                "success",
                "Subscription Created",
                response?.data?.message ||
                "Subscription created successfully. Payment is pending verification."
            );
            setRemarks("");
            await loadData();
            onSuccess?.();
        } catch (err) {
            showPopup?.("error", "Error", err?.response?.data?.message || err?.message || "Unable to create subscription.");
        } finally {
            setCreating(false);
        }
    };

    const handleVerifyPayment = (subscription, status) => {
        setVerificationItem(subscription);
        setVerificationStatus(status);
    };

    const confirmPaymentVerification = async () => {
        if (!verificationItem || !verificationStatus) return;
        const subscriptionId = verificationItem.rfId;
        const paymentId = verificationItem.payment_id || verificationItem.payment_rfId;
        if (!subscriptionId || !paymentId) {
            showPopup?.("error", "Missing Information", "Subscription ID or payment ID is missing from the API response.");
            return;
        }
        try {
            setVerifyingId(subscriptionId);
            const payload = {
                payment_id: paymentId,
                status: verificationStatus,
                remarks:
                    verificationStatus === "success"
                        ? "Payment verified by admin"
                        : "Payment rejected by admin",
            };
            const response = await verifySubscriptionPayment(
                subscriptionId,
                payload
            );
            if (response?.data?.success === false) {
                throw new Error(
                    response?.data?.message || "Payment verification failed."
                );
            }
            const verifiedSuccessfully = verificationStatus === "success";
            setVerificationItem(null);
            setVerificationStatus(null);
            showPopup?.(
                "success",
                verifiedSuccessfully
                    ? "Payment Verified"
                    : "Payment Rejected",
                response?.data?.message ||
                (verifiedSuccessfully
                    ? "Payment verified and subscription activated."
                    : "Payment rejected successfully.")
            );
            await loadData();
            onSuccess?.();
        } catch (err) {
            showPopup?.(
                "error",
                "Verification Failed",
                err?.response?.data?.message ||
                err?.message ||
                "Unable to update payment status."
            );
        } finally {
            setVerifyingId(null);
        }
    };

    const openInvoice = (item) => {
        if (String(item.payment_status).toLowerCase() !== "success") {
            showPopup?.(
                "error",
                "Invoice Not Available",
                "The invoice is available only after successful payment verification."
            );
            return;
        }
        setInvoiceItem(item);
    };

    const closeVerification = () => {
        if (verifyingId !== null) return;
        setVerificationItem(null);
        setVerificationStatus(null);
    };

    return (
        <>
            <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/50 p-3 sm:p-5">
                <div className="flex max-h-[95vh] w-full max-w-5xl flex-col overflow-hidden rounded-2xl bg-white shadow-2xl">
                    <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 sm:px-6">
                        <div>
                            <h2 className="text-lg font-bold text-gray-800">Member Subscription Management</h2>
                            <p className="mt-1 text-sm text-gray-500">Create subscriptions, verify payments and print GST bills.</p>
                        </div>
                        <button type="button" onClick={onClose} className="rounded-lg p-2 text-gray-500 transition hover:bg-gray-100 hover:text-gray-800">
                            <X size={21} />
                        </button>
                    </div>
                    <div className="overflow-y-auto p-4 sm:p-6">
                        <div className="mb-5 rounded-xl border border-blue-100 bg-blue-50/70 p-4">
                            <div className="flex items-center gap-3">
                                <div className="flex h-11 w-11 items-center justify-center rounded-full bg-blue-100 text-blue-600">
                                    <User size={21} />
                                </div>
                                <div>
                                    <h3 className="font-semibold text-gray-800">{memberName}</h3>
                                    <p className="text-sm text-gray-500">Member ID: {memberId} | Mobile: {memberMobile}</p>
                                </div>
                            </div>
                        </div>
                        {loading ? (
                            <div className="flex min-h-[180px] flex-col items-center justify-center text-gray-500">
                                <Loader2 className="mb-3 animate-spin text-blue-600" size={30} />
                                <p className="text-sm">Loading membership details...</p>
                            </div>
                        ) : (
                            <>
                                {error && (
                                    <div className="mb-5 flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                                        <AlertCircle size={18} className="mt-0.5 shrink-0" />
                                        <span>{error}</span>
                                    </div>
                                )}
                                <div className="mb-6 rounded-xl border border-gray-200 p-4 sm:p-5">
                                    <div className="mb-5 flex items-center gap-2">
                                        <CreditCard className="text-blue-600" size={20} />
                                        <h3 className="font-bold text-gray-800">Create New Subscription</h3>
                                    </div>
                                    <form onSubmit={handleCreateSubscription}>
                                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                                            <div className="sm:col-span-2">
                                                <label className="mb-2 block text-sm font-medium text-gray-700">Membership Plan</label>
                                                <select
                                                    value={selectedPlan}
                                                    onChange={(e) => setSelectedPlan(e.target.value)}
                                                    disabled={membershipPlans.length === 0 || creating}
                                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm outline-none transition focus:border-blue-500 focus:ring-2 focus:ring-blue-100 disabled:bg-gray-100">
                                                    <option value="">Select membership plan</option>
                                                    {membershipPlans.map((plan) => (
                                                        <option key={plan.rfId} value={plan.rfId}>
                                                            {plan.plan_name_en} - ₹
                                                            {formatAmount(plan.pricing?.final_amount ?? plan.amount)}
                                                        </option>
                                                    ))}
                                                </select>
                                                {membershipPlans.length === 0 && (
                                                    <p className="mt-2 text-sm text-orange-600">No active membership plans available. Please activate a plan first.</p>
                                                )}
                                            </div>
                                            {selectedPlanData && (
                                                <div className="sm:col-span-2 rounded-lg border border-blue-100 bg-blue-50/50 p-4">
                                                    <h4 className="mb-3 text-sm font-semibold text-gray-800">Subscription Amount Details</h4>
                                                    <div className="space-y-2 text-sm">
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-600">Plan Amount</span>
                                                            <span className="font-medium text-gray-800">₹{formatAmount(pricing?.base_amount ?? selectedPlanData.amount)}</span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-600">Discount</span>
                                                            <span className="font-medium text-green-600">- ₹{formatAmount(pricing?.discount_amount ?? selectedPlanData.discount_amount)}</span>
                                                        </div>
                                                        <div className="flex justify-between">
                                                            <span className="text-gray-600">GST ({pricing?.gst_percentage ?? selectedPlanData.gst_percentage ?? 0}%)</span>
                                                            <span className="font-medium text-gray-800">₹{formatAmount(pricing?.gst_amount ?? 0)}</span>
                                                        </div>
                                                        <div className="flex justify-between border-t border-blue-200 pt-3">
                                                            <span className="font-bold text-gray-800">Final Amount</span>
                                                            <span className="text-lg font-bold text-blue-700">₹{formatAmount(finalAmount)}</span>
                                                        </div>
                                                        <div className="flex justify-between text-xs text-gray-500">
                                                            <span>Duration</span>
                                                            <span>{selectedPlanData.duration_days} days</span>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}
                                            <div>
                                                <label className="mb-2 block text-sm font-medium text-gray-700">Payment Mode</label>
                                                <select
                                                    value={paymentMode}
                                                    onChange={(e) => setPaymentMode(e.target.value)}
                                                    disabled={creating}
                                                    className="w-full rounded-lg border border-gray-300 bg-white px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100">
                                                    <option value="cash">Cash</option>
                                                    <option value="upi">UPI</option>
                                                </select>
                                            </div>
                                            <div>
                                                <label className="mb-2 block text-sm font-medium text-gray-700">Remarks</label>
                                                <textarea
                                                    rows={1}
                                                    value={remarks}
                                                    onChange={(e) => setRemarks(e.target.value)}
                                                    disabled={creating}
                                                    placeholder="Optional remarks"
                                                    className="w-full resize-y rounded-lg border border-gray-300 px-4 py-3 text-sm outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100" />
                                            </div>
                                        </div>
                                        <div className="mt-5 flex justify-end">
                                            <button
                                                type="submit"
                                                disabled={creating || !selectedPlan || membershipPlans.length === 0}
                                                className="inline-flex items-center gap-2 rounded-lg bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-50">
                                                {creating ? (
                                                    <Loader2 size={17} className="animate-spin" />
                                                ) : (
                                                    <CreditCard size={17} />
                                                )}
                                                {creating ? "Creating..." : "Create Subscription"}
                                            </button>
                                        </div>
                                    </form>
                                </div>
                                <div className="rounded-xl border border-gray-200">
                                    <div className="flex flex-wrap items-center justify-between gap-3 border-b border-gray-200 px-4 py-4 sm:px-5">
                                        <div className="flex items-center gap-2">
                                            <Clock size={20} className="text-blue-600" />
                                            <h3 className="font-bold text-gray-800">Subscription History</h3>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={loadData}
                                            disabled={loading}
                                            className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm text-gray-600 hover:bg-gray-50 disabled:opacity-50">
                                            <RefreshCw size={15} />
                                            Refresh
                                        </button>
                                    </div>
                                    {subscriptions.length === 0 ? (
                                        <div className="p-8 text-center">
                                            <Wallet size={35} className="mx-auto mb-3 text-gray-300" />
                                            <p className="font-medium text-gray-600">No subscription history found</p>
                                            <p className="mt-1 text-sm text-gray-400">Create a new subscription for this member.</p>
                                        </div>
                                    ) : (
                                        <div className="overflow-x-auto">
                                            <table className="w-full min-w-[950px] text-left text-sm">
                                                <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                                                    <tr>
                                                        <th className="px-4 py-3">Plan / ID</th>
                                                        <th className="px-4 py-3">Amount</th>
                                                        <th className="px-4 py-3">Payment Mode</th>
                                                        <th className="px-4 py-3">Payment Status</th>
                                                        <th className="px-4 py-3">Subscription</th>
                                                        <th className="px-4 py-3">Date</th>
                                                        <th className="px-4 py-3 text-center">Action</th>
                                                    </tr>
                                                </thead>
                                                <tbody className="divide-y divide-gray-100">
                                                    {subscriptions.map((item) => {
                                                        const paymentStatus = item.payment_status || "pending";
                                                        const subscriptionStatus = item.subscription_status || "pending";
                                                        const pending = String(paymentStatus).toLowerCase() === "pending";
                                                        const manualPayment = ["cash", "upi",].includes(
                                                            String(item.payment_mode || "").toLowerCase()
                                                        );
                                                        const canVerify = pending && manualPayment;
                                                        const busy = String(verifyingId) === String(item.rfId);
                                                        return (
                                                            <tr key={`${item.rfId}-${item.payment_id || ""}`} className="hover:bg-gray-50">
                                                                <td className="px-4 py-4">
                                                                    <p className="font-semibold text-gray-800">{item.plan_name_en || item.membership_plan_name || `Subscription #${item.rfId}`}</p>
                                                                    <p className="mt-1 text-xs text-gray-400">ID: {item.rfId}</p>
                                                                </td>
                                                                <td className="px-4 py-4 font-semibold text-gray-800">
                                                                    ₹{formatAmount(item.total_amount ?? item.paid_amount ?? item.plan_amount)}
                                                                </td>
                                                                <td className="px-4 py-4 capitalize text-gray-600">
                                                                    {item.payment_mode || "-"}
                                                                </td>
                                                                <td className="px-4 py-4">
                                                                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold capitalize ${statusClass(paymentStatus)}`}>
                                                                        {paymentStatus}
                                                                    </span>
                                                                </td>
                                                                <td className="px-4 py-4">
                                                                    <span className={`inline-flex rounded-full px-3 py-1 text-xs font-semibold capitalize ${statusClass(subscriptionStatus)}`} >
                                                                        {subscriptionStatus}
                                                                    </span>
                                                                </td>
                                                                <td className="whitespace-nowrap px-4 py-4 text-gray-500">
                                                                    {formatDate(
                                                                        item.created_at ||
                                                                        item.payment_date
                                                                    )}
                                                                </td>
                                                                <td className="px-4 py-4">
                                                                    <div className="flex items-center justify-center gap-2">
                                                                        {canVerify ? (
                                                                            <>
                                                                                <button
                                                                                    type="button"
                                                                                    disabled={busy}
                                                                                    onClick={() => handleVerifyPayment(item, "success")}
                                                                                    className="inline-flex items-center gap-1 rounded-lg bg-green-600 px-3 py-2 text-xs font-semibold text-white hover:bg-green-700 disabled:opacity-50">
                                                                                    <CheckCircle size={14} />
                                                                                    Verify
                                                                                </button>
                                                                                <button
                                                                                    type="button"
                                                                                    disabled={busy}
                                                                                    onClick={() => handleVerifyPayment(item, "failed")}
                                                                                    className="inline-flex items-center gap-1 rounded-lg bg-red-50 px-3 py-2 text-xs font-semibold text-red-600 hover:bg-red-100 disabled:opacity-50">
                                                                                    <XCircle size={14} />
                                                                                    Reject
                                                                                </button>
                                                                            </>
                                                                        ) : (
                                                                            String(paymentStatus).toLowerCase() ===
                                                                            "success" && (
                                                                                <button
                                                                                    type="button"
                                                                                    onClick={() =>openInvoice(item)}
                                                                                    className="inline-flex items-center gap-1 rounded-lg bg-blue-600 px-3 py-2 text-xs font-semibold text-white hover:bg-blue-700">
                                                                                    <FileText size={14} />
                                                                                    View Bill
                                                                                </button>
                                                                            )
                                                                        )}
                                                                    </div>
                                                                </td>
                                                            </tr>
                                                        );
                                                    })}
                                                </tbody>
                                            </table>
                                        </div>
                                    )}
                                </div>
                            </>
                        )}
                    </div>

                    <div className="flex justify-end border-t border-gray-200 px-5 py-4">
                        <button
                            type="button"
                            onClick={onClose}
                            className="rounded-lg border border-gray-300 px-5 py-2.5 text-sm font-medium text-gray-700 hover:bg-gray-50">
                            Close
                        </button>
                    </div>
                </div>
            </div>

            {verificationItem && (
                <div className="fixed inset-0 z-[120] flex items-center justify-center bg-black/60 p-4">
                    <div className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl">
                        <div className="flex items-center justify-between border-b px-5 py-4">
                            <h3 className="text-lg font-bold text-gray-800">Payment Verification</h3>
                            <button
                                type="button"
                                onClick={closeVerification}
                                disabled={verifyingId !== null}
                                className="rounded-lg p-2 text-gray-500 hover:bg-gray-100 disabled:opacity-50">
                                <X size={20} />
                            </button>
                        </div>
                        <div className="p-5">
                            <div className="mb-4 flex justify-center">
                                {verificationStatus === "success" ? (
                                    <div className="rounded-full bg-green-100 p-4 text-green-600">
                                        <CheckCircle size={35} />
                                    </div>
                                ) : (
                                    <div className="rounded-full bg-red-100 p-4 text-red-600">
                                        <XCircle size={35} />
                                    </div>
                                )}
                            </div>
                            <h4 className="mb-2 text-center text-lg font-bold text-gray-800">
                                {verificationStatus === "success"
                                    ? "Verify & Activate Subscription?"
                                    : "Reject Payment?"}
                            </h4>
                            <p className="mb-5 text-center text-sm text-gray-500">
                                {verificationStatus === "success"
                                    ? "Confirm that you have received the payment. The subscription will be activated after successful verification."
                                    : "Are you sure you want to reject this payment? The subscription will not be activated."}
                            </p>
                            <div className="space-y-3 rounded-xl bg-gray-50 p-4 text-sm">
                                <div className="flex justify-between gap-3">
                                    <span className="text-gray-500">Member</span>
                                    <span className="text-right font-semibold text-gray-800">
                                        {memberName}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">
                                        Subscription ID
                                    </span>
                                    <span className="font-semibold text-gray-800">
                                        #{verificationItem.rfId}
                                    </span>
                                </div>
                                <div className="flex justify-between">
                                    <span className="text-gray-500">
                                        Payment Mode
                                    </span>
                                    <span className="font-semibold capitalize text-gray-800">
                                        {verificationItem.payment_mode || "-"}
                                    </span>
                                </div>
                                <div className="flex justify-between border-t border-gray-200 pt-3">
                                    <span className="font-semibold text-gray-700">Total Amount</span>
                                    <span className="text-lg font-bold text-blue-700">
                                        ₹
                                        {formatAmount(
                                            verificationItem.total_amount ??
                                            verificationItem.paid_amount ??
                                            verificationItem.plan_amount
                                        )}
                                    </span>
                                </div>
                            </div>
                            <div className="mt-5 flex gap-3">
                                <button
                                    type="button"
                                    disabled={verifyingId !== null}
                                    onClick={closeVerification}
                                    className="flex-1 rounded-lg border border-gray-300 px-4 py-3 text-sm font-semibold text-gray-700 hover:bg-gray-50 disabled:opacity-50">
                                    Cancel
                                </button>
                                <button
                                    type="button"
                                    disabled={verifyingId !== null}
                                    onClick={confirmPaymentVerification}
                                    className={`flex flex-1 items-center justify-center gap-2 rounded-lg px-4 py-3 text-sm font-semibold text-white disabled:opacity-50 ${verificationStatus === "success"
                                        ? "bg-green-600 hover:bg-green-700"
                                        : "bg-red-600 hover:bg-red-700"
                                        }`}>
                                    {verifyingId !== null && (
                                        <Loader2 size={16} className="animate-spin" />
                                    )}
                                    {verifyingId !== null
                                        ? "Processing..."
                                        : verificationStatus === "success"
                                            ? "Yes, Verify"
                                            : "Yes, Reject"}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {invoiceItem && (
                <SubscriptionInvoice
                    subscription={invoiceItem}
                    member={member}
                    onClose={() => setInvoiceItem(null)}
                />
            )}
        </>
    );
};

export default MemberSubscriptionModal;